import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { COLORS_PALETTE_DEFAULT } from '../../constants';

import styles from './Legend.module.css';

export interface LegendItem {
  /** Unique key for the legend entry. */
  key: string;
  /** Display name shown next to the color swatch. */
  name: string;
  /** Color for the swatch circle. */
  color: string;
}

/**
 * Converts an array of series-like objects into {@link LegendItem}s.
 *
 * Accepts any object with `seriesKey` and `name` (and optional `color`).
 * When no color is provided, assigns from the default palette by index.
 */
export function toLegendItems(
  series: ReadonlyArray<{ seriesKey: string; name: string; color?: string }>,
): LegendItem[] {
  return series.map((s, i) => ({
    key: s.seriesKey,
    name: s.name,
    color: s.color ?? COLORS_PALETTE_DEFAULT[i % COLORS_PALETTE_DEFAULT.length],
  }));
}

export interface LegendProps {
  /** Series items to display in the legend. */
  items: LegendItem[];
  /** Keys of items that are currently hidden/inactive. */
  hiddenKeys?: Set<string>;
  /** Called when an item is clicked (the whole-item click). Receives the item's key. */
  onItemClick?: (key: string) => void;
  /**
   * Called when an item's color swatch is clicked, overriding {@link onItemClick} for that
   * narrow region. Falls back to `onItemClick` when omitted. Lets a consumer give the swatch
   * a distinct action (e.g. toggle visibility) from clicking the rest of the item.
   */
  onSwatchClick?: (key: string) => void;
  /**
   * Called when the pointer/focus enters an item (with its key) or leaves the
   * legend (with `null`). Lets the chart spotlight the hovered series.
   */
  onItemHover?: (key: string | null) => void;
}

/**
 * Props-driven chart legend.
 *
 * Renders a horizontal row of colored circles and labels.
 * Capped at two visible rows with scroll for overflow.
 *
 * When `onItemClick` is provided, items become interactive buttons. The color swatch is a
 * separate button that calls `onSwatchClick` (falling back to `onItemClick`), so a consumer
 * can give the swatch a distinct action from clicking the rest of the item. Items whose key
 * appears in `hiddenKeys` are rendered dimmed.
 *
 * @example
 * ```tsx
 * <Legend
 *   items={[
 *     { key: 'cpu', name: 'CPU Usage', color: '#4e79a7' },
 *     { key: 'mem', name: 'Memory', color: '#f28e2b' },
 *   ]}
 *   hiddenKeys={hiddenKeys}
 *   onItemClick={(key) => toggleHidden(key)}
 * />
 * ```
 */
export function Legend({ items, hiddenKeys, onItemClick, onSwatchClick, onItemHover }: LegendProps) {
  if (items.length === 0) {
    return null;
  }

  const isInteractive = !!onItemClick || !!onSwatchClick;

  return (
    <div
      className={styles.container}
      onMouseLeave={() => onItemHover?.(null)}
      onBlur={(e) => {
        // Clear the spotlight when focus leaves the legend entirely (not when moving
        // between items), mirroring onMouseLeave for keyboard users.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onItemHover?.(null);
        }
      }}
    >
      {items.map((item) => {
        const isHidden = hiddenKeys?.has(item.key) ?? false;
        const labelClass = isHidden ? `${styles.label} ${styles.labelHidden}` : styles.label;

        const swatch = (
          <svg width={10} height={10} aria-hidden>
            <circle
              className={isHidden ? styles.swatchCircleOff : styles.swatchCircleOn}
              cx={5}
              cy={5}
              r={4}
              fill={item.color}
              stroke={item.color}
              strokeWidth={2}
            />
          </svg>
        );
        const label = <span className={labelClass}>{item.name}</span>;

        if (isInteractive) {
          return (
            <div
              key={item.key}
              className={`${styles.item} ${styles.itemInteractive}`}
              onMouseEnter={() => onItemHover?.(item.key)}
              onFocus={() => onItemHover?.(item.key)}
            >
              <button
                className={`${styles.itemButton} ${styles.swatchButton}`}
                aria-pressed={!isHidden}
                aria-label={`Toggle ${item.name}`}
                onClick={() => (onSwatchClick ?? onItemClick)?.(item.key)}
                type="button"
              >
                {swatch}
              </button>
              <TooltipTrigger delay={500}>
                <Focusable>
                  <button className={styles.itemButton} onClick={() => onItemClick?.(item.key)} type="button">
                    {label}
                  </button>
                </Focusable>
                <Tooltip placement="bottom">{item.name}</Tooltip>
              </TooltipTrigger>
            </div>
          );
        }

        return (
          <TooltipTrigger key={item.key} delay={500}>
            <Focusable>
              <div className={styles.item} role="img" aria-label={item.name}>
                {swatch}
                {label}
              </div>
            </Focusable>
            <Tooltip placement="bottom">{item.name}</Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
}

Legend.displayName = 'Legend';
